import { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail, formatINR, formatDate, packageLabel, methodLabel } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card } from "../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Table,
  TableHeader,
  TableRow,
  TableHead,
  TableBody,
  TableCell,
} from "../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogTrigger,
  DialogDescription,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import { Plus, Search, FileUp, Download, Trash2, Edit3, Eye, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../lib/auth.jsx";
import { useNavigate } from "react-router-dom";
import EditCustomerDialog from "../components/EditCustomerDialog";

function AddCustomerDialog({ serviceType, catalog, onCreated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    customer_code: "",
    name: "",
    phone: "",
    address: "",
    package: "",
    notes: "",
  });
  const [loading, setLoading] = useState(false);

  const submit = async () => {
    setLoading(true);
    try {
      await api.post("/customers", { ...form, service_type: serviceType });
      toast.success("Customer added");
      setOpen(false);
      setForm({ customer_code: "", name: "", phone: "", address: "", package: "", notes: "" });
      onCreated?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    } finally {
      setLoading(false);
    }
  };

  const codeLabel = serviceType === "internet" ? "User ID" : "Membership Name";
  const packages = serviceType === "internet"
    ? (catalog?.internet_speeds || []).map((s) => ({ value: String(s), label: `${s} Mbps` }))
    : (catalog?.cable_packages || []).map((p) => ({ value: p.key, label: p.label }));

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="add-customer-btn" className="bg-red-600 hover:bg-red-700">
          <Plus className="h-4 w-4 mr-1" /> Add Customer
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg" data-testid="add-customer-dialog">
        <DialogHeader>
          <DialogTitle>Add {serviceType === "internet" ? "Internet" : "Cable TV"} Customer</DialogTitle>
          <DialogDescription>Fill in details. Package price is entered later at billing time.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="sm:col-span-2">
            <Label>{codeLabel}</Label>
            <Input data-testid="cust-code-input" value={form.customer_code} onChange={(e) => setForm({ ...form, customer_code: e.target.value })} />
          </div>
          <div>
            <Label>Name</Label>
            <Input data-testid="cust-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </div>
          <div>
            <Label>Phone</Label>
            <Input data-testid="cust-phone-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
          </div>
          <div className="sm:col-span-2">
            <Label>Address</Label>
            <Input data-testid="cust-address-input" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
          </div>
          <div className="sm:col-span-2">
            <Label>Package</Label>
            <Select value={form.package} onValueChange={(v) => setForm({ ...form, package: v })}>
              <SelectTrigger data-testid="cust-package-select"><SelectValue placeholder="Select package" /></SelectTrigger>
              <SelectContent>
                {packages.map((p) => (
                  <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="sm:col-span-2">
            <Label>Notes</Label>
            <Textarea data-testid="cust-notes-input" rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button data-testid="cust-save-btn" disabled={loading || !form.customer_code || !form.name || !form.phone || !form.package} onClick={submit} className="bg-red-600 hover:bg-red-700">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save customer"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function BulkUploadDialog({ serviceType, onDone }) {
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const upload = async () => {
    if (!file) return;
    setLoading(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/customers/bulk-upload?service_type=${serviceType}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(data);
      toast.success(`Added ${data.added}, skipped ${data.skipped}`);
      onDone?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Upload failed");
    } finally {
      setLoading(false);
    }
  };

  const downloadTemplate = async () => {
    try {
      const res = await api.get(`/customers/template/download?service_type=${serviceType}`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: res.headers["content-type"] });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${serviceType}_template.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Failed to download template");
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="bulk-upload-btn" variant="outline">
          <FileUp className="h-4 w-4 mr-1" /> Bulk Upload
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md" data-testid="bulk-upload-dialog">
        <DialogHeader>
          <DialogTitle>Bulk Upload — {serviceType === "internet" ? "Internet" : "Cable TV"}</DialogTitle>
          <DialogDescription>Upload an Excel (.xlsx) file with customers.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <Button data-testid="download-template-btn" variant="outline" onClick={downloadTemplate} className="w-full">
            <Download className="h-4 w-4 mr-1" /> Download template
          </Button>
          <Input data-testid="bulk-file-input" type="file" accept=".xlsx" onChange={(e) => setFile(e.target.files?.[0] || null)} />
          {result && (
            <div className="text-sm bg-gray-50 border border-gray-200 rounded p-3">
              <div className="text-green-700 font-medium">Added: {result.added}</div>
              <div className="text-gray-700">Skipped (duplicates): {result.skipped}</div>
              {result.errors?.length > 0 && (
                <div className="mt-2 text-red-600">
                  Errors:
                  <ul className="list-disc ml-5">
                    {result.errors.slice(0, 5).map((e, i) => (
                      <li key={i}>Row {e.row}: {e.error}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Close</Button>
          <Button data-testid="bulk-upload-submit" disabled={!file || loading} onClick={upload} className="bg-red-600 hover:bg-red-700">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Upload"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CustomersTable({ serviceType, refreshKey, onRefresh, catalog }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = { service_type: serviceType };
      if (q) params.q = q;
      if (statusFilter !== "all") params.status = statusFilter;
      const { data } = await api.get("/customers", { params });
      setCustomers(data);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line
  }, [serviceType, refreshKey, statusFilter]);

  // debounce search
  useEffect(() => {
    const t = setTimeout(() => fetchData(), 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [q]);

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this customer?")) return;
    try {
      await api.delete(`/customers/${id}`);
      toast.success("Deleted");
      onRefresh?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Delete failed");
    }
  };

  const codeHeader = serviceType === "internet" ? "User ID" : "Membership";

  return (
    <Card className="p-4">
      <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center justify-between mb-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
          <Input
            data-testid="customer-search-input"
            className="pl-9"
            placeholder={`Search by name, phone, ${serviceType === "internet" ? "user id" : "membership"}…`}
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>
        <div className="flex gap-2">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-36" data-testid="status-filter-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="paid">Paid</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="overflow-x-auto">
        <Table data-testid={`customers-table-${serviceType}`}>
          <TableHeader>
            <TableRow>
              <TableHead>{codeHeader}</TableHead>
              <TableHead>Name</TableHead>
              <TableHead>Phone</TableHead>
              <TableHead>Package</TableHead>
              <TableHead>Last Payment</TableHead>
              <TableHead className="text-right">Balance Due</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && customers.length === 0 && (
              <TableRow><TableCell colSpan={7} className="text-center text-gray-500 py-6"><Loader2 className="h-4 w-4 animate-spin inline" /></TableCell></TableRow>
            )}
            {!loading && customers.length === 0 && (
              <TableRow><TableCell colSpan={7} className="text-center text-gray-500 py-6">No customers yet.</TableCell></TableRow>
            )}
            {customers.map((c) => (
              <TableRow key={c.id} className="hover:bg-gray-50">
                <TableCell className="font-medium">{c.customer_code}</TableCell>
                <TableCell>{c.name}</TableCell>
                <TableCell>{c.phone}</TableCell>
                <TableCell>{packageLabel(c.service_type, c.package)}</TableCell>
                <TableCell>
                  {c.last_payment_method ? (
                    <div className="text-xs">
                      <div className="font-medium text-gray-800">{methodLabel(c.last_payment_method)}</div>
                      <div className="text-gray-500">{formatDate(c.last_payment_at)}</div>
                    </div>
                  ) : <span className="text-gray-400 text-sm">—</span>}
                </TableCell>
                <TableCell className="text-right tabular-nums">
                  {c.balance_due > 0 ? (
                    <span className="status-chip status-pending">{formatINR(c.balance_due)}</span>
                  ) : (
                    <span className="status-chip status-paid">Cleared</span>
                  )}
                </TableCell>
                <TableCell className="text-right">
                  <div className="inline-flex items-center gap-1">
                    <Button
                      data-testid={`view-customer-${c.id}`}
                      size="icon"
                      variant="ghost"
                      onClick={() => navigate(`/customers/${c.id}`)}
                      title="View transactions"
                    >
                      <Eye className="h-4 w-4 text-gray-600" />
                    </Button>
                    <Button
                      data-testid={`edit-customer-${c.id}`}
                      size="icon"
                      variant="ghost"
                      onClick={() => setEditing(c)}
                      title="Edit"
                    >
                      <Edit3 className="h-4 w-4 text-gray-600" />
                    </Button>
                    {user?.role === "admin" && (
                      <Button data-testid={`delete-customer-${c.id}`} size="icon" variant="ghost" onClick={() => handleDelete(c.id)} title="Delete">
                        <Trash2 className="h-4 w-4 text-red-600" />
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <EditCustomerDialog
        customer={editing}
        catalog={catalog}
        open={!!editing}
        onOpenChange={(v) => !v && setEditing(null)}
        onUpdated={() => { setEditing(null); onRefresh?.(); fetchData(); }}
      />
    </Card>
  );
}

export default function CustomersPage() {
  const [catalog, setCatalog] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [tab, setTab] = useState("internet");

  useEffect(() => {
    api.get("/catalog").then((r) => setCatalog(r.data));
  }, []);

  const bump = () => setRefreshKey((k) => k + 1);

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-display font-bold tracking-tight">Customers</h1>
          <p className="text-gray-500 mt-1 text-sm">Internet and Cable TV customers — separate billing.</p>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <TabsList className="bg-white border border-gray-200">
            <TabsTrigger value="internet" data-testid="tab-internet">Internet</TabsTrigger>
            <TabsTrigger value="cable" data-testid="tab-cable">Cable TV</TabsTrigger>
          </TabsList>
          <div className="flex gap-2">
            <BulkUploadDialog serviceType={tab} onDone={bump} />
            <AddCustomerDialog serviceType={tab} catalog={catalog} onCreated={bump} />
          </div>
        </div>

        <TabsContent value="internet" className="mt-4">
          <CustomersTable serviceType="internet" refreshKey={refreshKey} onRefresh={bump} catalog={catalog} />
        </TabsContent>
        <TabsContent value="cable" className="mt-4">
          <CustomersTable serviceType="cable" refreshKey={refreshKey} onRefresh={bump} catalog={catalog} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
